import {
  Divider,
  Form,
  PrimaryButton,
  Space,
  TertiaryButton,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

// View owns the form contract (Form Schema Ownership iron law).
export interface StatementItemFormSchema {
  description: string
  date: Nullable<moment.Moment>
  amount: string
}

export interface IStatementItemFormProps {
  isSaving: boolean
  onSubmit: (values: StatementItemFormSchema) => void
  onCancel: () => void
}

// Form content ONLY — the "Create statement item" ModalDialog wrapper is
// FS-owned (BHV-041).
export const StatementItemForm = ({
  isSaving,
  onSubmit,
  onCancel,
}: IStatementItemFormProps) => {
  const intl = useProcurifyIntl()

  const validationSchema = Yup.object({
    description: Yup.string().required(
      intl.formatMessage(messages.descriptionRequiredError)
    ),
    date: Yup.mixed().required(intl.formatMessage(messages.dateRequiredError)),
    amount: Yup.string().required(
      intl.formatMessage(messages.amountRequiredError)
    ),
  })

  const formik = useFormik<StatementItemFormSchema>({
    initialValues: {
      description: '',
      date: moment(),
      amount: '',
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      onSubmit(values)
      // The genuine in-flight indicator is the mutation's isSaving prop.
      setSubmitting(false)
    },
  })

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <Form.TextField
            id='description'
            name='description'
            label={intl.formatMessage(messages.descriptionLabel)}
            required
            autoFocus
          />
          {/* Statement dates can be in the past — unblock past days */}
          <Form.SingleDatePicker
            id='date'
            name='date'
            label={intl.formatMessage(messages.dateLabel)}
            required
            isOutsideRange={() => false}
          />
          <Form.TextField
            id='amount'
            name='amount'
            label={intl.formatMessage(messages.amountLabel)}
            placeholder={intl.formatMessage(messages.amountPlaceholder)}
            required
          />

          <Divider />
          <Space direction='horizontal' justify='end' align='center'>
            <TertiaryButton
              type='button'
              onClick={onCancel}
              disabled={isSaving}
            >
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' disabled={isSaving}>
              {intl.formatMessage(messages.saveButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

StatementItemForm.displayName = 'StatementItemForm'
